import { assert } from 'superstruct';
import httpClient from '../api/httpClient';
import {
  SwipeGamesItem,
  SwipeGamesItemSchema,
  SwipeGameListItem,
  SwipeGameResponseSchema,
  GameListConfig,
  SwipeGamesListSchema,
} from '../model/swipe';
import {
  getGameLaunchUrl,
  getGameListUrl,
} from '../../entities/gameLauncherUrl';
import { getLocalStore } from '../lib/localStore';
import { LocalStoreKeys } from '../enums/localStoreKeys';
import { LogiSDKConfig } from '../model/config';

interface ApiResponse {
  data: { configuration: string }[];
}

export const formatGameListData = (
  apiResponse: ApiResponse,
): SwipeGamesItem[] => {
  return apiResponse?.data.reduce<SwipeGamesItem[]>((acc, item) => {
    try {
      const config = JSON.parse(item.configuration);
      const gameItem: SwipeGamesItem = {
        gameIconUrl: config.gameIconUrl,
        gameDropdownUrl: config.gameDropdownUrl,
        gameId: config.gameId,
        hasPff: config.hasPff,
        colorGradient: config.colorGradient.map((color: string) =>
          color.toLowerCase(),
        ),
      };

      assert(gameItem, SwipeGamesItemSchema);
      acc.push(gameItem);
    } catch (error) {
      console.error('Error formatting game item:', error);
    }
    return acc;
  }, []);
};

export const mapResponseToGameList = (
  games?: SwipeGameListItem[] | null,
): SwipeGamesItem[] => {
  const items: SwipeGameListItem[] = games ?? [];

  return items.map((game) => {
    const colourGradient = (game.colourGradient || []).map((c) =>
      String(c).toLowerCase(),
    );

    const colorGradient = colourGradient.length
      ? colourGradient
      : ['#000000', '#ffffff'];

    return {
      gameIconUrl: game.iconImgUrl ?? '',
      gameDropdownUrl: game.largeIconImgUrl ?? '',
      gameId: game.gameId,
      hasPff: game.hasPff,
      colorGradient,
      tileImgUrl: game.tileImgUrl,
      displayName: game.displayName,
    } as SwipeGamesItem;
  });
};

export const filterAndOrderGamesByOverride = (
  games: SwipeGamesItem[],
  gamesOverride?: (string | number)[] | null,
): SwipeGamesItem[] => {
  if (!gamesOverride) {
    return games;
  }

  const gamesMap = new Map<number, SwipeGamesItem>(
    games.map((game) => [game.gameId, game]),
  );

  const seenIds = new Set<number>();
  const orderedGames: SwipeGamesItem[] = [];

  for (const rawId of gamesOverride) {
    const gameId = Number(rawId);

    if (seenIds.has(gameId)) {
      continue;
    }

    seenIds.add(gameId);

    const game = gamesMap.get(gameId);
    if (game) {
      orderedGames.push(game);
    }
  }

  return orderedGames;
};

export const fetchGameListV1 = async (
  swipeSDKConfig: LogiSDKConfig,
): Promise<SwipeGamesItem[]> => {
  if (!swipeSDKConfig.swipeGames?.gameListConfig) {
    throw new Error('Game list configuration is missing');
  }

  const gameListUrl = getGameLaunchUrl(
    swipeSDKConfig.swipeGames.gameListConfig as never,
  );
  const apiResponse = await httpClient('GET', gameListUrl, {
    ...swipeSDKConfig.swipeGames.gameListConfig.requestHeaders,
  });

  assert(apiResponse, SwipeGameResponseSchema);
  const allGames = formatGameListData(apiResponse);

  return filterAndOrderGamesByOverride(
    allGames,
    swipeSDKConfig.swipeGames.gameListConfig.gamesOverride,
  );
};

export const fetchGameListV2 = async (
  swipeSDKConfig: LogiSDKConfig,
): Promise<SwipeGamesItem[]> => {
  if (!swipeSDKConfig.swipeGames?.gameListConfig) {
    throw new Error('Game list configuration is missing');
  }

  const tenantToken: string =
    (swipeSDKConfig.tenantId as string) ||
    (swipeSDKConfig.swipeGames.gameListConfig.routeParams?.tenant as string) ||
    '';

  const localeParam = process.env.NX_DEFAULT_LOCALE || 'en-GB';

  const gameListUrl = getGameListUrl(
    swipeSDKConfig.swipeGames.gameListConfig as GameListConfig,
    tenantToken,
    localeParam,
  );

  const apiResponse = await httpClient('GET', gameListUrl, {
    ...swipeSDKConfig.swipeGames.gameListConfig.requestHeaders,
  });

  assert(apiResponse, SwipeGamesListSchema);

  const allGames = mapResponseToGameList(apiResponse?.games || []);

  const showFineData = getLocalStore(LocalStoreKeys.SHOW_FINE_DATA);

  let filteredGames = allGames;

  if (showFineData === 'true' || showFineData === true) {
    filteredGames = allGames.slice(0, 5);
  }

  return filterAndOrderGamesByOverride(
    filteredGames,
    swipeSDKConfig.swipeGames.gameListConfig.gamesOverride,
  );
};
